import { CallbackDetails } from './claim/callback.js';
import { DateRange } from './filter.js';
import Fulfillment from './fulfillment.js';
import { BenefitType, Month, SubBenefitType } from './fulfillmentMeta.js';
import { Payee, PayeeType } from './payee.js';
import { PaymentSchedule } from './paymentPattern.js';
import { Address } from './address.js';

export enum AccountType {
  Checking = 'checking',
  Savings = 'savings',
}

export enum PaymentStatus {
  Pending = 'pending',
  Authorized = 'authorized',
  Success = 'success',
  Failed = 'failed',
  Initiated = 'initiated',
  Canceled = 'canceled',
}

export enum PaymentMethod {
  Check = 'check',
  DirectDeposit = 'ach',
  External = 'external',
  PhysicalCard = 'physicalCard',
  VirtualCard = 'virtualCard',
  Zelle = 'zelle',
}

export enum UtilityType {
  Telephone = 'Telephone',
  Electric = 'Electric',
  Gas = 'Gas',
  WaterSewer = 'Water/Sewer',
  Oil = 'Oil',
  Propane = 'Propane',
  Trash = 'Trash',
  Wood = 'Wood',
  Other = 'Other',
}

export enum ExpenseType {
  Internet = 'Internet',
  Hotel = 'Hotel',
  Rent = 'Rent',
  SecurityDeposit = 'SecurityDeposit',
  Other = 'Other',
}

export enum MailingAddressType {
  Payee = 'payee',
  Applicant = 'applicant',
  /** Enhancement: Adding program, partner */
}

export type PaymentMailingAddress = Pick<
  Address,
  'addressLine1' | 'addressLine2' | 'city' | 'state' | 'zip' | 'careOf'
>;
export interface Payment {
  id: string;
  fulfillmentId: string;
  fulfillment?: Fulfillment;
  displayId: string;
  amount: number;
  status: PaymentStatus;
  method: PaymentMethod;
  payee: Payee;
  payeeType: PayeeType;
  payeeId: string;
  note?: string;
  details?: CallbackDetails;
  createdAt: Date;
  updatedAt?: Date;
  initiatedAt?: Date;
  completedAt?: Date;
  deactivatedAt?: Date;
  scheduledFor?: Date;
  mailingAddressType?: MailingAddressType;
  mailingAddress?: PaymentMailingAddress;
}
export default Payment;

export interface PaymentInput {
  fundId: string;
  payeeType: PayeeType;
  payeeId: string;
  amount: number;
  method?: PaymentMethod;
  note?: string;
  mailingAddressType?: MailingAddressType;
  mailingAddress?: PaymentMailingAddress;

  // Recurring payment fields
  count?: number;
  pattern?: PaymentSchedule;
  start?: Date;

  // NH-only fields
  utilityType?: UtilityType;
  expenseType?: ExpenseType;
  status?: PaymentStatus;
  months?: Month[];
  benefit?: BenefitType;
  type?: SubBenefitType;
  billingCode?: string;
  checkNumber?: string;
  accountNumber?: string;
  completedAt?: Date;
  serviceDate?: string;
  startDate?: string;
  endDate?: string;
}

export interface CreatePaymentInput {
  caseId: string;
  payment: PaymentInput;
}

export interface UpdatePaymentInput {
  id: string;
  payment: PaymentInput;
}

export type RemovePaymentInput = { id: string };

export interface AggregatePayments {
  count: number;
  sum: number;
}

export type PaymemtRelationType = 'vendor';

export interface PaymentRelation {
  type: PaymemtRelationType;
  id: string;
}

export enum PaymentSortColumn {
  CheckDate = 'CheckDate',
}

export interface AggregatePaymentFilter {
  programId?: string;
  status?: PaymentStatus[];
  completedAt?: DateRange;
  initiatedAt?: DateRange;
  applicantIds?: string[];
  payeeIds?: string[];
}

export interface AggregatePayments {
  count: number;
  sum: number;
}

export type PopulatedPayment = Payment & { payee: Payee; fulfillment: Fulfillment };

export const obligatedPaymentStatuses = [PaymentStatus.Authorized, PaymentStatus.Failed];
export const successfulPaymentStatuses = [PaymentStatus.Initiated, PaymentStatus.Success];

export interface ResetPaymentInput {
  id: string;
  reason: string;
}
